import type { TrackingContextValue } from '@ps/web-shared-analytics';
import { TrackingContext, rewardsImpression } from '@ps/web-shared-analytics';
import classNames from 'clsx';
import type { ComponentType } from 'react';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import DetailsPanels from '@swf/components/StarsRewards/components/DetailsPanels/DetailsPanels';
import ExchangePanel from '@swf/components/StarsRewards/components/ExchangePanel/ExchangePanel';
import useEmbeddedNavigation from '@swf/core/hooks/useEmbeddedNavigation';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { ChestOpening } from '@swf/core/types/Rewards';
import type { EmbeddedNavigationProps } from '@swf/core/types/Rewards';
import type { RewardsStateModel } from '@swf/core/types/RewardsLegacy';

import styles from './PanelDropDown.scss';

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = ReturnType<typeof mapDispatch>;

interface OwnProps {
  data: RewardsStateModel;
  expanded?: boolean;
  outsideClickIgnoreClass?: string;
  clickOutside(): void;
  onMouseEnter?(): void;
  onMouseLeave?(): void;
}

interface State {
  showExchangePanel: boolean;
}

interface HookProps {
  openChest: (embeddedProps: EmbeddedNavigationProps) => void;
}

type PanelDropDownProps = OwnProps & HookProps;

type Props = PanelDropDownProps & StateProps & DispatchProps;

// TODO: refactor to FC
class PanelDropDown extends React.PureComponent<Props, State> {
  static contextType = TrackingContext;

  state = { showExchangePanel: false };

  handleClickOutside = () => {
    if (this.props.clickOutside) {
      this.props.clickOutside();
    }
  };

  componentDidMount() {
    rewardsImpression(this.context as TrackingContextValue, this.props.data);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      exchangeStatus,
      data: { cvlConfig }
    } = prevProps;

    if (exchangeStatus.response?.success) {
      this.onOpenChestClick();
      this.props.setExchangeStatus({ response: null });
      this.setState({ showExchangePanel: false });
    } else if (exchangeStatus.response && !exchangeStatus.response.success) {
      this.props.setExchangeStatus({ response: null });
      this.setState({ showExchangePanel: false });
    }
    if (cvlConfig && !cvlConfig.canExchange && prevState.showExchangePanel) {
      this.setState({ showExchangePanel: false });
    }
  }

  onOpenChestClick = () => {
    const { expanded, openChest } = this.props;
    if (!expanded) {
      this.handleClickOutside();
    }

    openChest(ChestOpening);
  };

  showExchange = () => {
    this.setState({ showExchangePanel: true });
  };

  cancelExchange = () => {
    this.setState({ showExchangePanel: false });
  };

  render() {
    const { onMouseEnter, onMouseLeave, expanded, data } = this.props;

    return (
      <div
        className={classNames(styles.details, { [styles.expanded]: expanded })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {this.state.showExchangePanel && data.cvlConfig?.canExchange ? (
          <ExchangePanel
            panelId={7}
            cancelExchange={this.cancelExchange}
            buttonClass={styles.chests}
            onOpenChestClick={this.onOpenChestClick}
          />
        ) : (
          <DetailsPanels
            showExchange={this.showExchange}
            onOpenChestClick={this.onOpenChestClick}
            data={data}
          />
        )}
      </div>
    );
  }
}

const mapState = (rootState: RootState) => ({
  loading: !!rootState.loading?.models.rewardsLegacy,
  exchangeStatus: select.rewardsLegacy.getExchangeStatus(rootState)
});

const mapDispatch = ({ rewardsLegacy: { setExchangeStatus } }: Dispatch) => ({
  setExchangeStatus
});

const PanelDropdownComponent = compose(
  // @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363
  connect<StateProps, DispatchProps, PanelDropDownProps>(mapState, mapDispatch)
)(PanelDropDown) as ComponentType<PanelDropDownProps>;

export default (props: OwnProps) => {
  const open = useEmbeddedNavigation();
  return <PanelDropdownComponent {...props} openChest={open} />;
};
